import { createApiHooks, useGenericGet, useGenericPost } from '../../../../../hooks/use-api.hook';
import { apiClient } from '../../../../../services/api-client.service';
import type {
  Job,
  JobCreatePayload,
  JobGetFiltersResponse,
  JobGetResponse,
} from '../types/job.types';
import type { UseListParams } from '../../../../../types/pagination.types';
import { useMemo } from 'react';
import { useActiveTenant } from '@states/active-tenant.atom';

const useJobBasePath = () => {
  const { activeTenant } = useActiveTenant();

  return useMemo(() => {
    return `/v1/admin/tenants/${activeTenant?.id}/jobs`;
  }, [activeTenant?.id]);
};

export const useJobHooks = () => {
  const jobBasePath = useJobBasePath();
  const { activeTenant } = useActiveTenant();

  return createApiHooks<Job, JobGetResponse, any, any>({
    basePath: jobBasePath,
    queryKey: ['Job', 'Tenant', activeTenant!.id],
    apiClient,
  });
};

export const useJobGetFilters = (queryOptions?: any) => {
  const jobBasePath = useJobBasePath();
  return useGenericGet<JobGetFiltersResponse>(
    `${jobBasePath}/filters`,
    ['Job', 'filters'],
    apiClient,
    queryOptions
  );
};

// Create a custom hook that returns all the job hooks
export const useJobApi = () => {
  const jobHooks = useJobHooks();

  return {
    useCreate: jobHooks.useCreate,
    useGet: jobHooks.useGet,
    useDelete: jobHooks.useDelete,
    useList: (params: UseListParams, queryOptions?: any) => jobHooks.useList(params, queryOptions),
  };
};

// Export individual hooks for backward compatibility
export const useJobGet = (id: string | number, queryOptions?: any) => {
  const { useGet } = useJobApi();
  return useGet(id, queryOptions);
};

export const useJobDelete = (mutationOptions?: any) => {
  const { useDelete } = useJobApi();
  return useDelete(mutationOptions);
};

export const useJobList = (params: UseListParams, queryOptions?: any) => {
  const { useList } = useJobApi();
  return useList(params, queryOptions);
};

export const useJobCreate = (mutationOptions?: any) => {
  const { useCreate } = useJobApi();
  return useCreate(mutationOptions);
};
